import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Trophy, Plus, Edit2, Trash2, Loader2, X, Copy, ExternalLink, Download,
  Image as ImageIcon, BarChart3, Eye, EyeOff, GripVertical, Trash, BookOpen,
  Upload, List, ToggleLeft, Type, Hash as HashIcon
} from 'lucide-react';

const FRONTEND_BASE = window.location.origin;

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown List' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'file', label: 'File / Image Upload' },
];

const ResultForms = () => {
  const [forms, setForms] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [posterUploading, setPosterUploading] = useState(null);
  const [exportingFormId, setExportingFormId] = useState(null);
  const [exportingWithFilesId, setExportingWithFilesId] = useState(null);

  const handleExport = async (form, includeFiles = false) => {
    if (includeFiles) setExportingWithFilesId(form.id); else setExportingFormId(form.id);
    try {
      const res = await api.exportResultFormSubmissions(form.id, includeFiles);
      const isZip = includeFiles;
      const blob = new Blob([res.data], {
        type: isZip
          ? 'application/zip'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      // Try to read filename from Content-Disposition
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/i.exec(cd);
      const fname = match ? match[1] : `${form.slug || 'results'}.${isZip ? 'zip' : 'xlsx'}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      const total = res.headers?.['x-total-rows'];
      if (isZip) {
        const included = res.headers?.['x-files-included'] || '0';
        const failed = res.headers?.['x-files-failed'] || '0';
        toast.success(
          `Downloaded ${total || ''} submissions with ${included} file(s)${Number(failed) > 0 ? ` (${failed} failed)` : ''}`
        );
      } else {
        const skipped = res.headers?.['x-excluded-file-fields'];
        toast.success(
          `Downloaded ${total || ''} submissions${skipped ? ` (skipped file fields: ${skipped})` : ''}`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      if (includeFiles) setExportingWithFilesId(null); else setExportingFormId(null);
    }
  };
  const [showNewExam, setShowNewExam] = useState(false);
  const [newExamName, setNewExamName] = useState('');
  const [creatingExam, setCreatingExam] = useState(false);

  const blankForm = {
    exam_id: '', slug: '', title: '', subtitle: '',
    cta_text: 'Submit My Result', primary_color: '#7c3aed',
    success_message: '', footer_text: '',
    show_roll_number: true, show_rank: true,
    allow_document: true, custom_fields: [],
  };
  const [formData, setFormData] = useState(blankForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [formsRes, examsRes] = await Promise.all([api.getResultForms(), api.getResultExams()]);
      setForms(formsRes.data);
      setExams(examsRes.data.exams || []);
    } catch (err) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingForm(null);
    setFormData(blankForm);
    setShowModal(true);
  };

  const openEdit = (f) => {
    setEditingForm(f);
    setFormData({
      exam_id: f.exam_id || '', slug: f.slug || '', title: f.title || '', subtitle: f.subtitle || '',
      cta_text: f.cta_text || 'Submit My Result', primary_color: f.primary_color || '#7c3aed',
      success_message: f.success_message || '', footer_text: f.footer_text || '',
      show_roll_number: f.show_roll_number !== false,
      show_rank: f.show_rank !== false,
      allow_document: f.allow_document !== false,
      custom_fields: f.custom_fields || [],
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.exam_id) { toast.error('Pick an exam'); return; }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (!payload.slug.trim()) {
        const exam = exams.find(x => x.id === payload.exam_id);
        if (exam) payload.slug = exam.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      if (editingForm) {
        await api.updateResultForm(editingForm.id, payload);
        toast.success('Form updated');
      } else {
        await api.createResultForm(payload);
        toast.success('Form created');
      }
      setShowModal(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete form "${f.title}"? Submissions remain in Results.`)) return;
    try { await api.deleteResultForm(f.id); toast.success('Form deleted'); await load(); }
    catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (f) => {
    try {
      await api.updateResultForm(f.id, { is_enabled: !f.is_enabled });
      toast.success(f.is_enabled ? 'Form paused' : 'Form activated');
      await load();
    } catch { toast.error('Failed to update'); }
  };

  const handlePosterUpload = async (form, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setPosterUploading(form.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.uploadResultFormPoster(form.id, fd);
      toast.success('Poster uploaded');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setPosterUploading(null); }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${FRONTEND_BASE}/result/${slug}`);
    toast.success('Link copied');
  };

  // Custom field management
  const addField = (typeOverride = 'text') => {
    const defaults = {
      text: 'Short Text Question',
      textarea: 'Long Text Question',
      number: 'Number',
      email: 'Email',
      date: 'Date',
      select: 'Pick from Dropdown',
      boolean: 'Yes / No Question',
      file: 'Upload File / Image',
    };
    setFormData(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, {
        key: `field_${Date.now()}`,
        label: defaults[typeOverride] || 'New Field',
        type: typeOverride,
        required: false,
        options: typeOverride === 'select' ? ['Option 1', 'Option 2'] : []
      }]
    }));
  };
  const updateField = (idx, patch) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.map((f, i) => i === idx ? { ...f, ...patch } : f)
    }));
  };
  const removeField = (idx) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== idx)
    }));
  };
  // Drag-and-drop reordering of custom fields
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const moveField = (from, to) => {
    if (from === to || from == null || to == null) return;
    setFormData(prev => {
      const arr = [...prev.custom_fields];
      const [picked] = arr.splice(from, 1);
      arr.splice(to, 0, picked);
      return { ...prev, custom_fields: arr };
    });
  };

  const handleCreateExam = async () => {
    const name = newExamName.trim();
    if (name.length < 2) {
      toast.error('Enter a valid exam name');
      return;
    }
    setCreatingExam(true);
    try {
      const res = await api.createResultExam({ name });
      const newExam = res.data;
      setExams(prev => [newExam, ...prev]);
      setFormData(prev => ({ ...prev, exam_id: newExam.id }));
      setNewExamName('');
      setShowNewExam(false);
      toast.success(`Exam "${newExam.name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create exam');
    } finally {
      setCreatingExam(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="result-forms-page" className="max-w-[1400px] mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-wide">Result Forms</h1>
          <p className="text-slate-500 text-sm max-w-2xl">
            Build public lead-style forms for each exam. Students fill the link, submissions land in Results at <strong>Written Cleared</strong> stage.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="create-result-form-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
        >
          <Plus size={18} /> New Form
        </button>
      </div>

      {forms.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
          <Trophy size={56} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No forms yet</h3>
          <p className="text-slate-500 mb-6">Create your first exam result form.</p>
          <button onClick={openCreate} className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            Create your first form
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {forms.map((form) => (
            <div key={form.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid={`result-form-card-${form.slug}`}>
              <div
                className="h-32 relative flex items-center justify-center text-white font-semibold"
                style={{ background: form.poster_url ? `url(${form.poster_url}) center/cover` : form.primary_color }}
              >
                {!form.poster_url && <span className="text-base px-4 text-center drop-shadow">{form.exam_name}</span>}
                {!form.is_enabled && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm font-bold uppercase">Paused</div>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-semibold text-slate-900 text-sm leading-tight mb-1 line-clamp-2">{form.title}</h3>
                <p className="text-xs text-slate-500 font-mono mb-3">/result/{form.slug}</p>
                <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-slate-500">Submissions</div>
                    <div className="text-lg font-bold text-slate-900">{form.submission_count || 0}</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-slate-500">Status</div>
                    <div className={`text-sm font-semibold ${form.is_enabled ? 'text-green-600' : 'text-slate-400'}`}>
                      {form.is_enabled ? 'Active' : 'Paused'}
                    </div>
                  </div>
                </div>
                <div className="mt-auto flex flex-wrap items-center gap-1.5">
                  <button onClick={() => copyLink(form.slug)} className="flex-1 min-w-[80px] flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100" title="Copy link" data-testid={`copy-link-${form.slug}`}>
                    <Copy size={12} /> Copy Link
                  </button>
                  <a href={`/result/${form.slug}`} target="_blank" rel="noopener noreferrer" title="Open form" className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200">
                    <ExternalLink size={12} />
                  </a>
                  <label className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 cursor-pointer" title="Upload poster">
                    {posterUploading === form.id ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { handlePosterUpload(form, e.target.files[0]); e.target.value = ''; }} />
                  </label>
                  <button onClick={() => toggleActive(form)} className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200" title={form.is_enabled ? 'Pause' : 'Activate'}>
                    {form.is_enabled ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                  <button onClick={() => openEdit(form)} className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200" title="Edit" data-testid={`edit-${form.slug}`}>
                    <Edit2 size={12} />
                  </button>
                  <button onClick={() => handleExport(form, false)}
                          disabled={exportingFormId === form.id}
                          className="px-2 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 disabled:opacity-60 inline-flex items-center gap-1"
                          title="Download submissions as Excel (excludes uploaded files)"
                          data-testid={`export-${form.slug}`}>
                    {exportingFormId === form.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    <span className="hidden sm:inline">Excel</span>
                  </button>
                  <button onClick={() => handleExport(form, true)}
                          disabled={exportingWithFilesId === form.id}
                          className="px-2 py-1.5 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-60 inline-flex items-center gap-1"
                          title="Download Excel + all uploaded images/files as ZIP"
                          data-testid={`export-with-files-${form.slug}`}>
                    {exportingWithFilesId === form.id ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                    <span className="hidden sm:inline">Excel + Images</span>
                  </button>
                  <button onClick={() => handleDelete(form)} className="px-2 py-1.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100" title="Delete">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">{editingForm ? 'Edit Form' : 'Create Result Form'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">Exam *</label>
                  <button
                    type="button"
                    onClick={() => { setShowNewExam(s => !s); setNewExamName(''); }}
                    data-testid="add-exam-toggle"
                    className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                  >
                    <Plus size={12} /> {showNewExam ? 'Cancel' : 'Add New Exam'}
                  </button>
                </div>
                {showNewExam ? (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <BookOpen size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={newExamName}
                        onChange={(e) => setNewExamName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateExam(); } }}
                        placeholder="e.g., SSC CGL 2026"
                        data-testid="new-exam-name"
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        autoFocus
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCreateExam}
                      disabled={creatingExam}
                      data-testid="new-exam-save"
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5 text-sm"
                    >
                      {creatingExam ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                      Add
                    </button>
                  </div>
                ) : (
                  <select
                    value={formData.exam_id}
                    onChange={(e) => setFormData({ ...formData, exam_id: e.target.value })}
                    data-testid="result-form-exam"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Select an exam</option>
                    {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                )}
                {exams.length === 0 && !showNewExam && (
                  <p className="text-xs text-amber-600 mt-1">No exams yet — click <strong>Add New Exam</strong> above.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  URL Slug
                  <span className="text-xs font-normal text-slate-500 ml-2">
                    {FRONTEND_BASE}/result/<span className="font-mono">{formData.slug || '...'}</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="auto-generated from exam name if empty"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Form Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="auto-generated if empty"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={formData.primary_color} onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })} className="h-10 w-16 rounded border border-slate-300 cursor-pointer" />
                    <input type="text" value={formData.primary_color} onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sub-headline</label>
                <textarea
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CTA Button Text</label>
                  <input type="text" value={formData.cta_text} onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Footer Note</label>
                  <input type="text" value={formData.footer_text} onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Success Message (shown after submission)</label>
                <textarea
                  value={formData.success_message}
                  onChange={(e) => setFormData({ ...formData, success_message: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              {/* Built-in field toggles */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Built-in Optional Fields</div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.show_roll_number} onChange={(e) => setFormData({ ...formData, show_roll_number: e.target.checked })} className="rounded" />
                    Roll Number
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.show_rank} onChange={(e) => setFormData({ ...formData, show_rank: e.target.checked })} className="rounded" />
                    Rank
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.allow_document} onChange={(e) => setFormData({ ...formData, allow_document: e.target.checked })} className="rounded" />
                    Scorecard Upload
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-2">Name + Phone are always required.</p>
              </div>

              {/* Custom fields */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Additional Custom Fields</label>
                  <span className="text-xs text-slate-400">{formData.custom_fields.length} field{formData.custom_fields.length === 1 ? '' : 's'}</span>
                </div>

                {/* Quick-add buttons — most-used types get a one-click button so they're discoverable */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => addField('file')}
                    data-testid="quick-add-file-field"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold border-2 border-purple-200 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 hover:border-purple-300 transition"
                  >
                    <Upload size={14} /> File / Image
                  </button>
                  <button
                    type="button"
                    onClick={() => addField('select')}
                    data-testid="quick-add-select-field"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold border-2 border-blue-200 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition"
                  >
                    <List size={14} /> Dropdown
                  </button>
                  <button
                    type="button"
                    onClick={() => addField('boolean')}
                    data-testid="quick-add-boolean-field"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold border-2 border-emerald-200 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition"
                  >
                    <ToggleLeft size={14} /> Yes / No
                  </button>
                  <button
                    type="button"
                    onClick={() => addField('text')}
                    data-testid="quick-add-text-field"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold border-2 border-slate-200 bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 hover:border-slate-300 transition"
                  >
                    <Type size={14} /> Text
                  </button>
                </div>

                {formData.custom_fields.length === 0 ? (
                  <div className="text-xs text-slate-400 italic px-3 py-2 border border-dashed border-slate-200 rounded">
                    No custom fields yet — pick a type above to add one.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.custom_fields.map((f, idx) => (
                      <div
                        key={idx}
                        data-testid={`custom-field-row-${idx}`}
                        onDragOver={(e) => {
                          if (dragIndex === null) return;
                          e.preventDefault();
                          if (dragOverIndex !== idx) setDragOverIndex(idx);
                        }}
                        onDragLeave={() => {
                          if (dragOverIndex === idx) setDragOverIndex(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragIndex !== null) moveField(dragIndex, idx);
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        className={`bg-slate-50 border rounded p-2 space-y-2 transition-colors ${
                          dragIndex === idx ? 'opacity-50 border-purple-400' :
                          dragOverIndex === idx ? 'border-purple-500 bg-purple-50' :
                          'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            draggable
                            onDragStart={(e) => {
                              setDragIndex(idx);
                              try {
                                e.dataTransfer.effectAllowed = 'move';
                                // Firefox needs data to be set for drag to fire
                                e.dataTransfer.setData('text/plain', String(idx));
                              } catch (_) { /* no-op */ }
                            }}
                            onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                            title="Drag to reorder"
                            data-testid={`custom-field-drag-${idx}`}
                            className="cursor-grab active:cursor-grabbing p-1 -m-1 text-slate-400 hover:text-slate-700 select-none"
                          >
                            <GripVertical size={14} />
                          </span>
                          <input
                            type="text"
                            placeholder="Label (e.g. Marks Obtained)"
                            value={f.label}
                            onChange={(e) => updateField(idx, { label: e.target.value })}
                            className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded"
                          />
                          <select
                            value={f.type}
                            onChange={(e) => updateField(idx, { type: e.target.value })}
                            className="px-2 py-1 text-sm border border-slate-300 rounded bg-white"
                          >
                            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-slate-600">
                            <input type="checkbox" checked={f.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="rounded" />
                            Required
                          </label>
                          <button type="button" onClick={() => removeField(idx)} className="p-1 text-slate-400 hover:text-red-600">
                            <Trash size={14} />
                          </button>
                        </div>
                        {f.type === 'select' && (
                          <input
                            type="text"
                            placeholder="Options (comma-separated)"
                            value={(f.options || []).join(', ')}
                            onChange={(e) => updateField(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-result-form-btn" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                  {submitting && <Loader2 className="animate-spin" size={16} />}
                  {editingForm ? 'Save Changes' : 'Create Form'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultForms;
